// Append-only TrackingTouch journal.
//
// One TrackingTouch records one meaningful interaction with our surfaces
// (PHP marketing site, React quote app, fast-lane endpoint, Meta lead
// webhook ingestion). Once written, a touch is never mutated. Attribution
// is computed from the array; the array is the truth.
//
// Storage tiers (see TRACKING_REFACTOR_PLAN.md + docs/marketing/GCLID_GUARD.md):
//   SESSION cookie — single most recent touch only (~500 bytes), scoped to
//                    `.topaesthetics.com`, NO max-age. Carries the latest touch
//                    across our surfaces within ONE browsing session.
//   sessionStorage — full list for this session, per origin. Survives reloads
//                    within the session; cleared on tab/browser close.
//   Server-side    — durable record on Quote.touchHistory and
//                    Patient.touchHistory, deduped on write.
//
// Storage is SESSION-scoped on purpose: a touch (and its gclid) must never
// outlive the session and get attached to a later visitor / a different patient
// on a shared browser — that is the gclid-reuse bug the guard exists to catch.
// Cross-surface (PHP marketing site → funnel) hand-off is via the URL.
//
// Merge semantics on read (Phase 4 contract):
//   1. Read cookie's latest touch + localStorage's full list.
//   2. If cookie's touch isn't already in localStorage (by the dedupe
//      triple ts+surface+resolvedLeadOrigin), prepend it.
//   3. Apply per-submit dedupe (60s window + same key tracking IDs).
//   4. The merged list is the source of truth for that session; write it
//      back to localStorage on every append.

// DELIBERATELY NARROWER than the server's TOUCH_SURFACES
// (server/models/touchSchema.ts): this union lists only the surfaces the
// CLIENT can construct a touch for. Server-only-stamped surfaces —
// "partner_api" (Partner Leads API) and "livestream" (joinLiveAsGuest) — are
// omitted on purpose: no client code path ever builds those touches, and
// keeping them out means a client bug can't forge one.
export type Surface =
  | "php_site"
  | "app_quote"
  | "funnel_fast_lane"
  | "meta_lead_webhook"
  | "import";

export type TouchTrackingIds = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  dclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  ltclid?: string;
  twclid?: string;
  gad_campaignid?: string;
  gad_adgroupid?: string;
  gad_source?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  fb_campaign_id?: string;
  fb_adset_id?: string;
  fb_ad_id?: string;
  metaCampaignName?: string;
  metaCampaignId?: string;
  metaAdsetName?: string;
  metaAdsetId?: string;
  metaAdName?: string;
  metaAdId?: string;
};

export type TrackingTouch = {
  ts: string;
  surface: Surface;
  landingPath: string;
  landingQuery: Record<string, string>;
  referrer: string;
  trackingIds: TouchTrackingIds;
  resolvedLeadOrigin: string;
  formId?: string;
};

// Allowed keys for landingQuery. Raw URLs are never stored — query
// strings sometimes contain emails, tokens, or other content we shouldn't
// persist. Add a key here when a new tracking parameter needs to round-
// trip through the touch.
export const LANDING_QUERY_ALLOWLIST = [
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "fbclid",
  "ttclid",
  "msclkid",
  "twclid",
  "li_fat_id",
  "ltclid",
  "gad_source",
  "gad_campaignid",
  "gad_adgroupid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "lead_origin",
] as const;

// Keys used to determine whether two touches are "the same event" for the
// per-submit (Quote-level) dedupe rule D2. Enumerated explicitly so both
// the TS and PHP implementations stay in lockstep.
const DEDUPE_TRACKING_KEYS = [
  "gclid",
  "fbclid",
  "gad_campaignid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
] as const;

const COOKIE_NAME = "ta_touch_latest";
const LOCALSTORAGE_KEY = "ta_touches";
const COOKIE_MAX_BYTES = 500;
const REFERRER_MAX_CHARS = 200;
const DEDUPE_WINDOW_MS = 60_000;
const SOFT_CAP = 50;

const getCookieDomainAttr = (): string => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname || "";
  return host.endsWith("topaesthetics.com")
    ? "; domain=.topaesthetics.com"
    : "";
};

// ─── Canonical serialization ────────────────────────────────────────────
//
// Used to compute stable dedupe keys that match across TS and PHP. NOT
// used for cookie/localStorage format — those are plain JSON. Canonical
// rules: sort object keys lexicographically; UTF-8 strings encoded as JSON
// strings (slashes not escaped, non-ASCII left as-is); arrays preserve
// order; no whitespace. Mirror in touch_history.php.
export function canonicalize(value: unknown): string {
  if (value === null || value === undefined) return "null";
  if (typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return "[" + value.map(canonicalize).join(",") + "]";
  }
  if (typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    return (
      "{" +
      keys
        .map(
          (k) =>
            JSON.stringify(k) +
            ":" +
            canonicalize((value as Record<string, unknown>)[k]),
        )
        .join(",") +
      "}"
    );
  }
  return "null";
}

// Per-submit dedupe key (D2 Quote rule). Two touches with this same key
// within 60s collapse into one on the server. The key is the same shape
// in TS and PHP — keep them in sync.
export function dedupeKey(touch: TrackingTouch): string {
  const ids: Record<string, string> = {};
  for (const k of DEDUPE_TRACKING_KEYS) {
    const v = touch.trackingIds[k];
    if (v) ids[k] = v;
  }
  return canonicalize({ origin: touch.resolvedLeadOrigin, ids });
}

// Exact identity key (D2 Patient cross-quote rule). Two touches matching
// this triple are the same regardless of time gap.
export function identityKey(touch: TrackingTouch): string {
  return `${touch.ts}::${touch.surface}::${touch.resolvedLeadOrigin}`;
}

// ─── Touch construction ─────────────────────────────────────────────────

function pickLandingQuery(
  searchParams: URLSearchParams,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of LANDING_QUERY_ALLOWLIST) {
    const v = searchParams.get(key);
    if (v) out[key] = v;
  }
  return out;
}

function pickTrackingIds(
  searchParams: URLSearchParams,
): TouchTrackingIds {
  const allowed: Array<keyof TouchTrackingIds> = [
    "gclid",
    "gbraid",
    "wbraid",
    "dclid",
    "fbclid",
    "ttclid",
    "msclkid",
    "li_fat_id",
    "ltclid",
    "twclid",
    "gad_campaignid",
    "gad_adgroupid",
    "gad_source",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
  ];
  const out: TouchTrackingIds = {};
  for (const key of allowed) {
    const v = searchParams.get(key);
    if (v) (out as Record<string, string>)[key] = v;
  }
  return out;
}

function truncateReferrer(raw: string): string {
  if (!raw) return "";
  try {
    const url = new URL(raw);
    const compact = `${url.host}${url.pathname}`;
    return compact.length > REFERRER_MAX_CHARS
      ? compact.slice(0, REFERRER_MAX_CHARS)
      : compact;
  } catch {
    return raw.slice(0, REFERRER_MAX_CHARS);
  }
}

// Build a Touch from the current URL + referrer + a resolved lead origin.
// Caller passes resolvedLeadOrigin so this module doesn't have to import
// the existing resolveLeadOrigin function (avoids a circular dep with
// tracking.ts).
export function buildTouch(opts: {
  surface: Surface;
  search: string;
  pathname: string;
  referrer: string;
  resolvedLeadOrigin: string;
  formId?: string;
  ts?: string;
  extraTrackingIds?: TouchTrackingIds;
}): TrackingTouch {
  const searchParams = new URLSearchParams(opts.search || "");
  const ids = pickTrackingIds(searchParams);
  if (opts.extraTrackingIds) {
    for (const [k, v] of Object.entries(opts.extraTrackingIds)) {
      if (v) (ids as Record<string, string>)[k] = v;
    }
  }
  return {
    ts: opts.ts ?? new Date().toISOString(),
    surface: opts.surface,
    landingPath: opts.pathname || "/",
    landingQuery: pickLandingQuery(searchParams),
    referrer: truncateReferrer(opts.referrer || ""),
    trackingIds: ids,
    resolvedLeadOrigin: opts.resolvedLeadOrigin,
    ...(opts.formId ? { formId: opts.formId } : {}),
  };
}

// ─── Dedupe ─────────────────────────────────────────────────────────────

export function dedupePerSubmit(touches: TrackingTouch[]): TrackingTouch[] {
  // Sort by ts ascending; ISO 8601 sorts lexicographically.
  const sorted = [...touches].sort((a, b) => a.ts.localeCompare(b.ts));
  const out: TrackingTouch[] = [];
  for (const t of sorted) {
    const key = dedupeKey(t);
    const tMs = Date.parse(t.ts);
    const collision = out.some((o) => {
      if (dedupeKey(o) !== key) return false;
      const oMs = Date.parse(o.ts);
      return Math.abs(tMs - oMs) <= DEDUPE_WINDOW_MS;
    });
    if (!collision) out.push(t);
  }
  return out;
}

export function dedupeCrossQuote(
  touches: TrackingTouch[],
): TrackingTouch[] {
  const seen = new Set<string>();
  const out: TrackingTouch[] = [];
  for (const t of touches) {
    const key = identityKey(t);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

// ─── Storage ────────────────────────────────────────────────────────────

function readCookieLatestTouch(): TrackingTouch | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"),
  );
  if (!m) return null;
  try {
    const decoded = decodeURIComponent(m[1]);
    const parsed = JSON.parse(decoded);
    if (parsed && typeof parsed === "object" && parsed.ts && parsed.surface) {
      return parsed as TrackingTouch;
    }
    return null;
  } catch {
    return null;
  }
}

function writeCookieLatestTouch(touch: TrackingTouch): void {
  if (typeof document === "undefined") return;
  // SESSION cookie (no max-age) — carries the latest touch across our surfaces
  // within the SAME browsing session only; it must never persist to a later
  // session or the gclid would bleed onto a different visitor. The fuller list
  // lives in sessionStorage; both clear on browser close.
  const payload = encodeURIComponent(JSON.stringify(touch));
  if (payload.length > COOKIE_MAX_BYTES) {
    // Touch too fat for the cookie budget — drop landingQuery to slim it
    // down. We never lose ids; landingQuery is the bulky one.
    const slim = { ...touch, landingQuery: {} };
    const slimPayload = encodeURIComponent(JSON.stringify(slim));
    if (slimPayload.length > COOKIE_MAX_BYTES) return; // give up; sessionStorage still has the full one
    document.cookie = `${COOKIE_NAME}=${slimPayload}; path=/; samesite=lax${getCookieDomainAttr()}`;
    return;
  }
  document.cookie = `${COOKIE_NAME}=${payload}; path=/; samesite=lax${getCookieDomainAttr()}`;
}

function readLocalStorageTouches(): TrackingTouch[] {
  if (typeof window === "undefined") return [];
  try {
    // sessionStorage, not localStorage — the touch journal is session-scoped so
    // a touch (and its gclid) can't outlive the session and get attached to a
    // later visitor / a different patient on a shared browser. See gclid guard.
    const raw = window.sessionStorage.getItem(LOCALSTORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t) =>
        t &&
        typeof t === "object" &&
        typeof t.ts === "string" &&
        typeof t.surface === "string" &&
        typeof t.resolvedLeadOrigin === "string",
    ) as TrackingTouch[];
  } catch {
    return [];
  }
}

function writeLocalStorageTouches(touches: TrackingTouch[]): void {
  if (typeof window === "undefined") return;
  try {
    // Soft-prune from the oldest end so we never exceed SOFT_CAP per
    // surface — keeps localStorage from growing unbounded across years.
    const trimmed =
      touches.length > SOFT_CAP ? touches.slice(touches.length - SOFT_CAP) : touches;
    window.sessionStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage may be unavailable (private mode, quota); we lose
    // resilience but the cookie still carries the latest touch.
  }
}

// ─── Public API ─────────────────────────────────────────────────────────

// Merge cookie's latest + localStorage's full list, dedupe, return the
// merged list. Does not write — pure read. Use this whenever you need to
// see all touches the current browser is aware of.
export function readTouches(): TrackingTouch[] {
  const local = readLocalStorageTouches();
  const fromCookie = readCookieLatestTouch();
  if (!fromCookie) return dedupePerSubmit(local);
  const alreadyHave = local.some(
    (t) => identityKey(t) === identityKey(fromCookie),
  );
  const merged = alreadyHave ? local : [fromCookie, ...local];
  return dedupePerSubmit(merged);
}

// Append a touch. Reads existing merged list, dedupes, writes merged list
// back to localStorage, writes the new touch to the cookie (if it
// survives dedupe). Returns the final merged list.
export function appendTouch(touch: TrackingTouch): TrackingTouch[] {
  const existing = readTouches();
  const merged = dedupePerSubmit([...existing, touch]);
  writeLocalStorageTouches(merged);
  // Only update the cookie if the new touch actually landed in the
  // merged list (i.e. wasn't dropped as a dedupe collision).
  const survived = merged.some(
    (t) => identityKey(t) === identityKey(touch),
  );
  if (survived) writeCookieLatestTouch(touch);
  return merged;
}

// Serialize merged touches for an outbound submit payload. Always uses
// canonical form so the server's dedupe key matches the client's.
export function serializeForSubmit(touches: TrackingTouch[]): string {
  return canonicalize(touches);
}
